import { useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';
import { PiTrashBold } from 'react-icons/pi';
import { useWatch } from 'react-hook-form';

import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import { Input } from '#/modules/commonComponents/form/Input';
import SalesOrderInventoryCandidatePickerDialog from '#/modules/crmMgmt/salesOrder/components/SalesOrderInventoryCandidatePickerDialog';
import {
  collectUsedInventoryStockIds,
  findSalesOrderInventoryCandidatesForMtr,
  getAllocationsForMaterialSnapshot,
  getSalesOrderAllocatedQtyValueClass,
  mapInventoryCandidateToSalesOrderAllocation,
  resolveSalesOrderMaterialMtrId,
  resolveSalesOrderMaterialRequiredQty,
  sumAllocationQty,
} from '#/modules/crmMgmt/salesOrder/helper/salesOrderAllocationHelpers';
import {
  getSalesOrderAllocationStockId,
  getSalesOrderLineInvAllocations,
  getSalesOrderLineMtrs,
  getSalesOrderLineQty,
} from '#/modules/crmMgmt/salesOrder/helper/salesOrderFieldAccessors';
import { draftGroupHeaderBarClassName } from '#/modules/momMgmt/workOrder/helper/draftGroupHeaderBarClassName';
import OutboundQtyStat from '#/modules/momMgmt/workOrder/outbound/components/OutboundQtyStat';
import { OUTBOUND_QTY_INPUT_CLASS } from '#/modules/momMgmt/workOrder/outbound/helper/outboundQtyStatClasses';
import { formatDisplayQty } from '#/utils/formatNumber';
import { clampQtyToAvailable } from '#/utils/quantityBounds';
import { toast } from '#/utils/toast';

function lineHasMtrs(line) {
  return getSalesOrderLineMtrs(line).length > 0;
}

function formatLineProductLabel(line) {
  const product = line?.product;
  if (!product) {
    return '—';
  }
  const code = String(product.productNo ?? product.code ?? '').trim();
  const name = String(product.name ?? product.productName ?? '').trim();
  if (code && name) {
    return `${code}／${name}`;
  }
  return code || name || '—';
}

function SalesOrderMaterialAllocationGroup({
  line,
  material,
  materialIndex,
  lineIndex,
  getValues,
  setValue,
  candidatesData,
  candidatesLoading,
  candidatesError,
}) {
  const mtrRowId = material?.id != null && material.id !== ''
    ? String(material.id)
    : null;
  const mtrId = resolveSalesOrderMaterialMtrId(material);
  const requiredQty = resolveSalesOrderMaterialRequiredQty(
    material,
    getSalesOrderLineQty(line),
  );
  const allocations = getAllocationsForMaterialSnapshot(line, mtrRowId);
  const allocatedQty = sumAllocationQty(allocations);
  const materialName = String(
    material?.mtrName
    ?? material?.material?.name
    ?? '',
  ).trim();
  const materialNo = String(
    material?.mtrNo
    ?? material?.material?.mtrNo
    ?? '',
  ).trim();

  const [open, setOpen] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);

  const candidates = findSalesOrderInventoryCandidatesForMtr(candidatesData, {
    lineId: line?.id,
    mtrRowId,
  });

  const initialSelectedIds = allocations
    .map((allocation) => getSalesOrderAllocationStockId(allocation))
    .filter(Boolean);

  function handleOpenPicker() {
    if (!mtrRowId) {
      toast.warning('物料列缺少 id，無法配置');
      return;
    }
    if (!line?.id) {
      toast.warning('銷貨明細缺少 id，無法對應候選');
      return;
    }
    setPickerKey((prev) => prev + 1);
    setPickerOpen(true);
  }

  function replaceMaterialAllocations(nextForMaterial) {
    const lines = getValues('lines') ?? [];
    const lineAllocations = getSalesOrderLineInvAllocations(lines[lineIndex]);
    const others = lineAllocations.filter(
      (allocation) => String(
        allocation?.salesOrderLineMtrId
        ?? allocation?.customerOrderMaterialSnapshotId,
      ) !== String(mtrRowId),
    );
    const nextLines = lines.map((item, index) => (
      index === lineIndex
        ? { ...item, invAllocations: [...others, ...nextForMaterial] }
        : item
    ));
    setValue('lines', nextLines, { shouldDirty: true });
  }

  function handlePickerConfirm(selectedCandidates) {
    const lines = getValues('lines') ?? [];
    const usedElsewhere = collectUsedInventoryStockIds(lines, {
      excludeLineIndex: lineIndex,
      excludeMtrRowId: mtrRowId,
    });

    const existingByStockId = new Map(
      allocations
        .filter((allocation) => getSalesOrderAllocationStockId(allocation))
        .map((allocation) => [
          String(getSalesOrderAllocationStockId(allocation)),
          allocation,
        ]),
    );

    const allowed = [];
    let skippedConflict = 0;
    (selectedCandidates ?? []).forEach((candidate) => {
      const stockId = candidate?.invStockId != null
        ? String(candidate.invStockId)
        : null;
      if (!stockId) {
        return;
      }
      if (usedElsewhere.has(stockId)) {
        skippedConflict += 1;
        return;
      }
      const kept = existingByStockId.get(stockId);
      allowed.push(
        mapInventoryCandidateToSalesOrderAllocation(
          candidate,
          mtrRowId,
          kept?.qty ?? '',
        ),
      );
    });

    if (skippedConflict > 0) {
      toast.warning(`已略過 ${skippedConflict} 筆：該庫存已在本銷貨單其他物料配置`);
    }

    replaceMaterialAllocations(allowed);
  }

  function handleQtyChange(stockId, nextQty) {
    const nextForMaterial = allocations.map((allocation) => (
      String(getSalesOrderAllocationStockId(allocation)) === String(stockId)
        ? { ...allocation, qty: nextQty }
        : allocation
    ));
    replaceMaterialAllocations(nextForMaterial);
  }

  function handleQtyBlur(stockId, rawValue) {
    const target = allocations.find(
      (allocation) => (
        String(getSalesOrderAllocationStockId(allocation)) === String(stockId)
      ),
    );
    if (!target) {
      return;
    }
    const nextQty = clampQtyToAvailable(rawValue, target.availableQty);
    if (String(target.qty) === String(nextQty)) {
      return;
    }
    handleQtyChange(stockId, nextQty);
  }

  function handleRemove(stockId) {
    replaceMaterialAllocations(
      allocations.filter(
        (allocation) => (
          String(getSalesOrderAllocationStockId(allocation)) !== String(stockId)
        ),
      ),
    );
  }

  return (
    <div className="overflow-hidden rounded-md border border-slate-200">
      <div className={draftGroupHeaderBarClassName({ open })}>
        <button
          type="button"
          className="
            flex min-w-0 flex-1 items-center gap-2 text-left transition-colors
            hover:text-primary-600
          "
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
        >
          <ChevronDown
            size={18}
            className={cn(
              'shortcut_transition-transform shrink-0 origin-center text-gray-600',
              open ? 'rotate-0' : '-rotate-90',
            )}
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-slate-800">
              {materialName || '（未命名）'}
            </p>
            {(materialNo || mtrId) ? (
              <p className="truncate text-xs text-muted-foreground">
                {materialNo || mtrId}
              </p>
            ) : null}
          </div>
        </button>
        <div className="flex shrink-0 items-center gap-2">
          <OutboundQtyStat
            label="需求"
            value={formatDisplayQty(requiredQty)}
          />
          <OutboundQtyStat
            label="已配"
            value={formatDisplayQty(allocatedQty)}
            valueClassName={getSalesOrderAllocatedQtyValueClass(
              allocatedQty,
              requiredQty,
            )}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            disabled={!mtrRowId || !line?.id}
            onClick={(event) => {
              event.stopPropagation();
              handleOpenPicker();
            }}
          >
            選擇庫存
          </Button>
        </div>
      </div>

      <div
        className={cn(
          'grid shortcut_transition-disclosure',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
        aria-hidden={!open}
      >
        <div className="min-h-0 overflow-hidden">
          {allocations.length === 0 ? (
            <p className="px-3 py-3 text-sm text-muted-foreground">
              尚無已選庫存
            </p>
          ) : (
            <ul className="m-0 list-none divide-y divide-slate-100 p-0">
              {allocations.map((allocation, allocationIndex) => {
                const stockId = getSalesOrderAllocationStockId(allocation);
                const warehouseLabel = allocation?.warehouse?.name
                  ?? allocation?.warehouseName;
                const stockLabel = allocation?.stockNo ?? stockId ?? '—';
                const availableQty = Number(allocation?.availableQty);
                const qtyMax = Number.isFinite(availableQty) && availableQty > 0
                  ? availableQty
                  : undefined;
                return (
                  <li
                    key={
                      stockId
                      ?? `${lineIndex}-${materialIndex}-allocation-${allocationIndex}`
                    }
                    className={cn(
                      'grid items-center gap-2 px-3 py-2.5',
                      'grid-cols-[minmax(0,1fr)_auto]',
                    )}
                  >
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm text-slate-700">
                        {stockLabel}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {warehouseLabel || '—'}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                        可用量：
                        {' '}
                        {formatDisplayQty(
                          Number.isFinite(availableQty)
                            ? availableQty
                            : allocation?.availableQty,
                        )}
                      </span>
                      <Input
                        type="number"
                        min={1}
                        max={qtyMax}
                        className={OUTBOUND_QTY_INPUT_CLASS}
                        value={allocation?.qty ?? ''}
                        onChange={(event) => {
                          handleQtyChange(stockId, event.target.value);
                        }}
                        onBlur={(event) => {
                          handleQtyBlur(stockId, event.target.value);
                        }}
                        aria-label={`${stockLabel} 配置數量`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
                        aria-label={`移除 ${stockLabel}`}
                        onClick={() => handleRemove(stockId)}
                      >
                        <PiTrashBold className="size-4" aria-hidden />
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <SalesOrderInventoryCandidatePickerDialog
        key={pickerKey}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        candidates={candidates}
        initialSelectedIds={initialSelectedIds}
        onConfirm={handlePickerConfirm}
        requiredQty={requiredQty ?? null}
        allocatedQty={allocatedQty ?? null}
        isLoading={candidatesLoading}
        isError={Boolean(candidatesError)}
        errorMessage={
          candidatesError?.message || '載入可配庫存失敗'
        }
      />
    </div>
  );
}

SalesOrderMaterialAllocationGroup.propTypes = {
  line: PropTypes.object,
  material: PropTypes.object.isRequired,
  materialIndex: PropTypes.number.isRequired,
  lineIndex: PropTypes.number.isRequired,
  getValues: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  candidatesData: PropTypes.object,
  candidatesLoading: PropTypes.bool,
  candidatesError: PropTypes.object,
};

function SalesOrderAllocationLineBlock({
  line,
  lineIndex,
  getValues,
  setValue,
  candidatesData,
  candidatesLoading,
  candidatesError,
}) {
  const [open, setOpen] = useState(true);
  const hasMtrs = lineHasMtrs(line);
  const mtrs = getSalesOrderLineMtrs(line);
  const productLabel = formatLineProductLabel(line);
  const salesQty = formatDisplayQty(getSalesOrderLineQty(line));
  const lineTitle = `第 ${lineIndex + 1} 列 · ${productLabel}`;

  return (
    <section className="overflow-hidden rounded-md border border-slate-200">
      <div className={draftGroupHeaderBarClassName({ open })}>
        <button
          type="button"
          className="
            flex min-w-0 flex-1 items-center gap-2 text-left transition-colors
            hover:text-primary-600
          "
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
        >
          <ChevronDown
            size={18}
            className={cn(
              'shortcut_transition-transform shrink-0 origin-center text-gray-600',
              open ? 'rotate-0' : '-rotate-90',
            )}
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-slate-800">
              {lineTitle}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              銷貨量
              {' '}
              {salesQty}
            </p>
          </div>
        </button>
      </div>

      <div
        className={cn(
          'grid shortcut_transition-disclosure',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
        aria-hidden={!open}
      >
        <div className="min-h-0 overflow-hidden">
          {!hasMtrs ? (
            <p className="px-3 py-3 text-sm text-muted-foreground">
              此列尚無物料（mtrs），無法配置
            </p>
          ) : (
            <div className="space-y-2 p-3">
              {mtrs.map((material, materialIndex) => (
                <SalesOrderMaterialAllocationGroup
                  key={material?.id ?? `mtr-${materialIndex}`}
                  line={line}
                  material={material}
                  materialIndex={materialIndex}
                  lineIndex={lineIndex}
                  getValues={getValues}
                  setValue={setValue}
                  candidatesData={candidatesData}
                  candidatesLoading={candidatesLoading}
                  candidatesError={candidatesError}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

SalesOrderAllocationLineBlock.propTypes = {
  line: PropTypes.object,
  lineIndex: PropTypes.number.isRequired,
  getValues: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  candidatesData: PropTypes.object,
  candidatesLoading: PropTypes.bool,
  candidatesError: PropTypes.object,
};

/**
 * 銷貨單庫存配置：巢狀 SO 列 → mtr → 庫存（candidates）。
 */
const SalesOrderAllocationEditor = ({
  control,
  getValues,
  setValue,
  candidatesData = null,
  candidatesLoading = false,
  candidatesError = null,
}) => {
  const watchedLines = useWatch({ control, name: 'lines', defaultValue: [] });
  const lines = Array.isArray(watchedLines) ? watchedLines : [];
  // 其他產品（manualLines）不進 lines[]；產品列無 mtrs 亦略過
  const allocatableEntries = lines
    .map((line, index) => ({ line, index }))
    .filter(({ line }) => lineHasMtrs(line));

  return (
    <section className="pb-6">
      <div className="form-block">
        <h2 className="form-block__title">庫存配置</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          請為各物料挑選庫存並填數量。同一張銷貨單不可重複選同一筆庫存。
          備貨中儲存時只更新庫存配置。其他產品與無物料列不需配置。
        </p>
      </div>

      {allocatableEntries.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">
          尚無可配置的產品明細（無物料列）
        </p>
      ) : (
        <div className="mt-3 space-y-3">
          {allocatableEntries.map(({ line, index }) => (
            <SalesOrderAllocationLineBlock
              key={line?.id ?? `line-${index}`}
              line={line}
              lineIndex={index}
              getValues={getValues}
              setValue={setValue}
              candidatesData={candidatesData}
              candidatesLoading={candidatesLoading}
              candidatesError={candidatesError}
            />
          ))}
        </div>
      )}
    </section>
  );
};

SalesOrderAllocationEditor.propTypes = {
  control: PropTypes.object.isRequired,
  getValues: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  candidatesData: PropTypes.object,
  candidatesLoading: PropTypes.bool,
  candidatesError: PropTypes.object,
};

export default SalesOrderAllocationEditor;
